import {
	AttachmentWithSource,
	ImportEmailAttachmentPayload,
	ImportEmailAttachmentsResponse,
	RelatedEntityType,
} from "@dune/types";
import { getErrorMessage } from "@dune/vars";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useEmailStore } from "../../stores/email.store";
import { privateAxios } from "../axios";
import { communicationsQueryKeys } from "../query-keys/communications.query-keys";
import { constructionSitesQueryKeys } from "../query-keys/constructionSites.query-keys";
import { propositionsQueryKeys } from "../query-keys/propositions.query-keys";
import { societiesQueryKeys } from "../query-keys/societies.query-keys";
import { SaveDraftEmailPayload } from "../types/mail.types";
import { gfasQueryKeys } from "../query-keys/gfas.query-keys";

export interface SendEmailPayload {
	to: string[];
	cc?: string[];
	bcc?: string[];
	subject: string;
	html: string;
	attachments?: AttachmentWithSource[];
	entityType: RelatedEntityType;
	entityId: number;
	draftId?: number;
	replyTo?: string;
}

const sendEmail = async (payload: SendEmailPayload) => {
	const { data } = await privateAxios.post("/mail/send", payload);
	return data;
};

export const useSendEmail = ({
	entityId,
	entityType,
}: {
	entityId: number;
	entityType: RelatedEntityType;
}) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: sendEmail,
		onSuccess: () => {
			toast.success("Email envoyé avec succès");

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});

			if (entityType === "BROKER") {
				queryClient.invalidateQueries({
					queryKey: societiesQueryKeys.brokerCommunicationsAll(entityId),
				});
				queryClient.invalidateQueries({
					queryKey: ["broker-unprocessed-communications-count", entityId],
				});
			} else if (entityType === "CONSTRUCTION_SITE") {
				queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.communicationsAll(entityId),
				});
				queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.unprocessedCommunicationsCount(
						entityId.toString(),
					),
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "construction-sites" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId.toString(),
				});
			} else if (entityType === "PROPOSITION") {
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.all,
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "propositions" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId,
				});
				queryClient.invalidateQueries({
					queryKey:
						propositionsQueryKeys.unprocessedCommunicationsCount(entityId),
				});
			}
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'envoi de l'email", {
				description: errorMessage,
			});
		},
	});
};

const saveDraftEmail = async (payload: SaveDraftEmailPayload) => {
	const { data } = await privateAxios.post("/mail/draft", payload);
	return data;
};

export const useSaveDraftEmail = ({
	entityId,
	entityType,
}: {
	entityId: number;
	entityType: RelatedEntityType;
}) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: saveDraftEmail,
		onSuccess: () => {
			toast.success("Brouillon sauvegardé");

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});

			if (entityType === "BROKER") {
				queryClient.invalidateQueries({
					queryKey: societiesQueryKeys.brokerCommunicationsAll(entityId),
				});
				queryClient.invalidateQueries({
					queryKey: ["broker-unprocessed-communications-count", entityId],
				});
			} else if (entityType === "CONSTRUCTION_SITE") {
				queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.communicationsAll(entityId),
				});
				queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.unprocessedCommunicationsCount(
						entityId.toString(),
					),
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "construction-sites" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId.toString(),
				});
			} else if (entityType === "PROPOSITION") {
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.all,
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "propositions" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId,
				});
				queryClient.invalidateQueries({
					queryKey:
						propositionsQueryKeys.unprocessedCommunicationsCount(entityId),
				});
			}
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la sauvegarde du brouillon", {
				description: errorMessage,
			});
		},
	});
};

/**
 * Delete a draft email by ID
 */
const deleteDraftEmail = async (id: number) => {
	const { data } = await privateAxios.delete(`/mail/draft/${id}`);
	return data;
};

/**
 * Hook to delete a draft email
 */
export const useDeleteDraftEmail = ({
	entityId,
	entityType,
	onSuccess: onSuccessCallback,
}: {
	entityId: number;
	entityType?: RelatedEntityType;
	onSuccess?: () => void;
}) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: deleteDraftEmail,
		onSuccess: () => {
			toast.success("Brouillon supprimé");

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});

			if (entityType === "BROKER") {
				queryClient.invalidateQueries({
					queryKey: societiesQueryKeys.brokerCommunicationsAll(entityId),
				});
			} else if (entityType === "CONSTRUCTION_SITE") {
				queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.communicationsAll(entityId),
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "construction-sites" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId.toString(),
				});
			} else if (entityType === "PROPOSITION") {
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.all,
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "propositions" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId,
				});
			} else if (entityType === "GFA") {
				queryClient.invalidateQueries({
					queryKey: gfasQueryKeys.communicationsAll(entityId),
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "gfas" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId,
				});
			}

			onSuccessCallback?.();
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la suppression du brouillon", {
				description: errorMessage,
			});
		},
	});
};

export interface UpdateDraftEmailPayload
	extends Omit<SaveDraftEmailPayload, "draftId"> {
	draftId: number;
}

const updateDraftEmail = async (payload: UpdateDraftEmailPayload) => {
	const { draftId, ...rest } = payload;
	const { data } = await privateAxios.patch(`/mail/draft/${draftId}`, rest);
	return data;
};

export const useUpdateDraftEmail = ({
	entityId,
	entityType,
}: {
	entityId: number;
	entityType: RelatedEntityType;
}) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: updateDraftEmail,
		onSuccess: () => {
			toast.success("Brouillon mis à jour");

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});

			if (entityType === "BROKER") {
				queryClient.invalidateQueries({
					queryKey: societiesQueryKeys.brokerCommunicationsAll(entityId),
				});
				queryClient.invalidateQueries({
					queryKey: ["broker-unprocessed-communications-count", entityId],
				});
			} else if (entityType === "CONSTRUCTION_SITE") {
				queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.communicationsAll(entityId),
				});
				queryClient.invalidateQueries({
					queryKey: constructionSitesQueryKeys.unprocessedCommunicationsCount(
						entityId.toString(),
					),
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "construction-sites" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId.toString(),
				});
			} else if (entityType === "PROPOSITION") {
				queryClient.invalidateQueries({
					queryKey: propositionsQueryKeys.all,
				});
				queryClient.invalidateQueries({
					predicate: (query) =>
						query.queryKey[0] === "propositions" &&
						query.queryKey[1] === "communications" &&
						query.queryKey[2] === entityId,
				});
				queryClient.invalidateQueries({
					queryKey:
						propositionsQueryKeys.unprocessedCommunicationsCount(entityId),
				});
			}
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de la mise à jour du brouillon", {
				description: errorMessage,
			});
		},
	});
};

export interface UpdateProcessedPayload {
	brokerId: number;
	emailId: number;
	isProcessed: boolean;
}

const updateProcessed = async ({
	brokerId,
	emailId,
	isProcessed,
}: UpdateProcessedPayload) => {
	const { data } = await privateAxios.patch(
		`/societies/broker/${brokerId}/communications/${emailId}/processed`,
		{ isProcessed },
	);
	return data;
};

export const useUpdateProcessedEmail = (brokerId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: { emailId: number; isProcessed: boolean }) =>
			updateProcessed({
				brokerId,
				emailId: payload.emailId,
				isProcessed: payload.isProcessed,
			}),
		onSuccess: (_, variables) => {
			const { selectedEmailId, actions } = useEmailStore.getState();

			if (selectedEmailId === variables.emailId.toString()) {
				actions.clearEmailSelection();
			}

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});

			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerCommunicationsAll(brokerId),
			});
			queryClient.invalidateQueries({
				queryKey: ["broker-unprocessed-communications-count", brokerId],
			});
		},
	});
};

const importEmailAttachments = async ({
	brokerId,
	emailAttachmentId,
}: ImportEmailAttachmentPayload): Promise<ImportEmailAttachmentsResponse> => {
	const { data } = await privateAxios.post(
		`/societies/broker/${brokerId}/import-attachment/${emailAttachmentId}`,
	);
	return data;
};

export const useImportEmailAttachments = (brokerId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: { emailAttachmentId: number }) =>
			importEmailAttachments({
				brokerId,
				emailAttachmentId: payload.emailAttachmentId,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerDocuments(brokerId),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerCommunicationsAll(brokerId),
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});
			toast.success("Pièces jointes importées avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'importation des pièces jointes", {
				description: errorMessage,
			});
		},
	});
};

export const useArchiveCommunication = (brokerId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ emailId }: { emailId: number }) =>
			privateAxios.patch(
				`/societies/broker/${brokerId}/communications/${emailId}/archive`,
				{ isArchived: true },
			),
		onSuccess: (_, variables) => {
			toast.success("Communication archivée");

			const { selectedEmailId, actions } = useEmailStore.getState();

			if (selectedEmailId === variables.emailId.toString()) {
				actions.clearEmailSelection();
			}

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: societiesQueryKeys.brokerCommunicationsAll(brokerId),
			});
			queryClient.invalidateQueries({
				queryKey: ["broker-unprocessed-communications-count", brokerId],
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'archivage de la communication", {
				description: errorMessage,
			});
		},
	});
};

const importEmailAttachmentsConstructionSite = async ({
	constructionSiteId,
	emailAttachmentId,
}: {
	constructionSiteId: number;
	emailAttachmentId: number;
}): Promise<ImportEmailAttachmentsResponse> => {
	const { data } = await privateAxios.post(
		`/constructionSites/${constructionSiteId}/import-attachment/${emailAttachmentId}`,
	);
	return data;
};

export const useImportEmailAttachmentsConstructionSite = (
	constructionSiteId: number,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: { emailAttachmentId: number }) =>
			importEmailAttachmentsConstructionSite({
				constructionSiteId,
				emailAttachmentId: payload.emailAttachmentId,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey:
					constructionSitesQueryKeys.communicationsAll(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});
			toast.success("Pièces jointes importées avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'importation des pièces jointes", {
				description: errorMessage,
			});
		},
	});
};

const importEmailAttachmentsGFA = async ({
	gfaId,
	emailAttachmentId,
}: {
	gfaId: number;
	emailAttachmentId: number;
}): Promise<ImportEmailAttachmentsResponse> => {
	const { data } = await privateAxios.post(
		`/gfa/${gfaId}/import-attachment/${emailAttachmentId}`,
	);
	return data;
};

export const useImportEmailAttachmentsGFA = (gfaId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: { emailAttachmentId: number }) =>
			importEmailAttachmentsGFA({
				gfaId,
				emailAttachmentId: payload.emailAttachmentId,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.communicationsAll(gfaId),
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: gfasQueryKeys.documents(gfaId),
			});
			toast.success("Pièces jointes importées avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'importation des pièces jointes", {
				description: errorMessage,
			});
		},
	});
};

const importEmailAttachmentsProposition = async ({
	propositionId,
	emailAttachmentId,
}: {
	propositionId: number;
	emailAttachmentId: number;
}): Promise<ImportEmailAttachmentsResponse> => {
	const { data } = await privateAxios.post(
		`/propositions/${propositionId}/import-attachment/${emailAttachmentId}`,
	);
	return data;
};

export const useImportEmailAttachmentsProposition = (propositionId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: { emailAttachmentId: number }) =>
			importEmailAttachmentsProposition({
				propositionId,
				emailAttachmentId: payload.emailAttachmentId,
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.communications(propositionId),
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.documents(propositionId),
			});
			toast.success("Pièces jointes importées avec succès");
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'importation des pièces jointes", {
				description: errorMessage,
			});
		},
	});
};

export interface UpdateProcessedPayloadConstructionSite {
	constructionSiteId: number;
	emailId: number;
	isProcessed: boolean;
	isGFA: boolean;
}

const updateProcessedConstructionSite = async ({
	constructionSiteId,
	emailId,
	isProcessed,
	isGFA,
}: UpdateProcessedPayloadConstructionSite) => {
	const { data } = await privateAxios.patch(
		`${isGFA ? "/gfa/" : "/constructionSites/"}${constructionSiteId}/communications/${emailId}/processed`,
		{ isProcessed },
	);
	return data;
};

export const useUpdateProcessedEmailConstructionSite = (
	constructionSiteId: number,
	isGFA: boolean,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: { emailId: number; isProcessed: boolean }) =>
			updateProcessedConstructionSite({
				constructionSiteId,
				emailId: payload.emailId,
				isProcessed: payload.isProcessed,
				isGFA,
			}),
		onSuccess: (_, variables) => {
			const { selectedEmailId, actions } = useEmailStore.getState();

			if (selectedEmailId === variables.emailId.toString()) {
				actions.clearEmailSelection();
			}

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});

			queryClient.invalidateQueries({
				queryKey: isGFA
					? gfasQueryKeys.communicationsAll(constructionSiteId)
					: constructionSitesQueryKeys.communicationsAll(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey: isGFA
					? gfasQueryKeys.unprocessedCommunicationsCount(
							constructionSiteId.toString(),
						)
					: constructionSitesQueryKeys.unprocessedCommunicationsCount(
							constructionSiteId.toString(),
						),
			});
		},
	});
};

export const useArchiveCommunicationConstructionSite = (
	constructionSiteId: number,
	isGFA: boolean,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ emailId }: { emailId: number }) =>
			privateAxios.patch(
				`${isGFA ? "/gfa/" : "/constructionSites/"}${constructionSiteId}/communications/${emailId}/archive`,
				{ isArchived: true },
			),
		onSuccess: (_, variables) => {
			toast.success("Communication archivée");

			const { selectedEmailId, actions } = useEmailStore.getState();

			if (selectedEmailId === variables.emailId.toString()) {
				actions.clearEmailSelection();
			}

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: isGFA
					? gfasQueryKeys.communicationsAll(constructionSiteId)
					: constructionSitesQueryKeys.communicationsAll(constructionSiteId),
			});
			queryClient.invalidateQueries({
				queryKey: isGFA
					? gfasQueryKeys.unprocessedCommunicationsCount(
							constructionSiteId.toString(),
						)
					: constructionSitesQueryKeys.unprocessedCommunicationsCount(
							constructionSiteId.toString(),
						),
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'archivage de la communication", {
				description: errorMessage,
			});
		},
	});
};

export interface UpdateProcessedPayloadProposition {
	propositionId: number;
	emailId: number;
	isProcessed: boolean;
}

const updateProcessedProposition = async ({
	propositionId,
	emailId,
	isProcessed,
}: UpdateProcessedPayloadProposition) => {
	const { data } = await privateAxios.patch(
		`/propositions/${propositionId}/communications/${emailId}/processed`,
		{ isProcessed },
	);
	return data;
};

export const useUpdateProcessedEmailProposition = (propositionId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (payload: { emailId: number; isProcessed: boolean }) =>
			updateProcessedProposition({
				propositionId,
				emailId: payload.emailId,
				isProcessed: payload.isProcessed,
			}),
		onSuccess: (_, variables) => {
			const { selectedEmailId, actions } = useEmailStore.getState();

			if (selectedEmailId === variables.emailId.toString()) {
				actions.clearEmailSelection();
			}

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});

			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "propositions" &&
					query.queryKey[1] === "communications" &&
					query.queryKey[2] === propositionId,
			});
			queryClient.invalidateQueries({
				queryKey:
					propositionsQueryKeys.unprocessedCommunicationsCount(propositionId),
			});
		},
	});
};

export const useArchiveCommunicationProposition = (propositionId: number) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: ({ emailId }: { emailId: number }) =>
			privateAxios.patch(
				`/propositions/${propositionId}/communications/${emailId}/archive`,
				{ isArchived: true },
			),
		onSuccess: (_, variables) => {
			toast.success("Communication archivée");

			const { selectedEmailId, actions } = useEmailStore.getState();

			if (selectedEmailId === variables.emailId.toString()) {
				actions.clearEmailSelection();
			}

			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				queryKey: communicationsQueryKeys.lists(),
			});
			queryClient.invalidateQueries({
				queryKey: propositionsQueryKeys.all,
			});
			queryClient.invalidateQueries({
				predicate: (query) =>
					query.queryKey[0] === "propositions" &&
					query.queryKey[1] === "communications" &&
					query.queryKey[2] === propositionId,
			});
			queryClient.invalidateQueries({
				queryKey:
					propositionsQueryKeys.unprocessedCommunicationsCount(propositionId),
			});
		},
		onError: (error) => {
			const errorMessage = getErrorMessage(error);
			toast.error("Erreur lors de l'archivage de la communication", {
				description: errorMessage,
			});
		},
	});
};
